import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Loader2, Save, Trash2, Plus, ArrowUpDown, Edit2, MoreHorizontal } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import CartCounter from '../components/CartCounter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const OrdersPage = () => {
  const { currentUser, currentUserProfile } = useAuth();
  const { cartItems, setCart, addToCart } = useCart();
  const navigate = useNavigate();
  const [savedOrders, setSavedOrders] = useState([]);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [orderName, setOrderName] = useState('');
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [viewOrderDialogOpen, setViewOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editOrderName, setEditOrderName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedItems, setEditedItems] = useState([]);

  // Calculate order total
  const calculateOrderTotal = (items) => {
    return items.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  // Fetch saved and previous orders
  useEffect(() => {
    if (!currentUser) return;

    // Listen to user document for orders
    const userDocRef = doc(db, 'users', currentUser.uid);
    const userUnsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        const savedOrders = userData.savedOrders || [];
        const previousOrders = userData.orders || [];
        
        console.log('Fetched orders from user document:', {
          savedOrders,
          previousOrders
        });

        setSavedOrders(savedOrders.map(order => ({
          ...order,
          items: order.items.map(item => ({
            ...item,
            quantity: parseInt(item.quantity) || 1
          }))
        })));

        setPreviousOrders(
          previousOrders
            .map(order => ({
              ...order,
              items: order.items.map(item => ({
                ...item,
                quantity: parseInt(item.quantity) || 1
              }))
            }))
            .sort((a, b) => {
              const getOrderTime = (order) => {
                const timeValue = order?.createdAt || order?.date || order?.orderDate;
                if (!timeValue) return 0;
                const parsedTime = new Date(timeValue).getTime();
                return Number.isNaN(parsedTime) ? 0 : parsedTime;
              };

              return getOrderTime(b) - getOrderTime(a);
            })
        );

        setLoading(false);
      }
    }, (error) => {
      console.error('Error fetching user orders:', error);
      toast.error('Failed to fetch orders');
    });

    return () => {
      userUnsubscribe();
    };
  }, [currentUser]);

  const handleSaveOrder = async () => {
    if (!orderName.trim()) {
      toast.error('Please enter an order name');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        toast.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const newOrder = {
        id: `SAVED-${new Date().toISOString().split('T')[0]}-${Math.random().toString(36).substr(2, 4)}`,
        name: orderName.trim(),
        invoiceReference: `SAVED-${new Date().toISOString().split('T')[0]}-${Math.random().toString(36).substr(2, 4)}`,
        items: cartItems.map(item => ({
          ...item,
          quantity: parseInt(item.quantity) || 1
        })),
        createdAt: new Date().toISOString()
      };

      // Add to savedOrders array in user document
      const updatedSavedOrders = [...(userData.savedOrders || []), newOrder];

      await updateDoc(userDocRef, {
        savedOrders: updatedSavedOrders
      });

      toast.success('Order saved successfully');
      setSaveDialogOpen(false);
      setOrderName('');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    }
  };

  const handleRestoreOrder = async (items) => {
    try {
      // Add items to cart
      items.forEach(item => {
        addToCart(item, item.quantity);
      });

      // If this is a previous order, move it to saved orders
      if (selectedOrder && !selectedOrder.name) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const previousOrders = userData.orders || [];
          const savedOrders = userData.savedOrders || [];
          
          // Find the order in previous orders
          const orderToSave = previousOrders.find(order => order.id === selectedOrder.id);
          
          if (orderToSave) {
            // Add to saved orders with a new name
            const newSavedOrder = {
              ...orderToSave,
              name: `Restored Order ${new Date().toLocaleDateString()}`,
              restoredAt: new Date().toISOString()
            };

            await updateDoc(userDocRef, {
              savedOrders: [...savedOrders, newSavedOrder]
            });
          }
        }
      }

      toast.success('Order restored to cart');
      navigate('/');
    } catch (error) {
      console.error('Error restoring order:', error);
      toast.error('Failed to restore order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        toast.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const updatedSavedOrders = (userData.savedOrders || []).filter(order => order.id !== orderId);
      
      await updateDoc(userDocRef, {
        savedOrders: updatedSavedOrders
      });
      
      toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setEditOrderName(order.name);
    const itemsWithParsedQuantities = order.items.map(item => {
      const parsedItem = {
        ...item,
        quantity: parseInt(item.quantity) || 1
      };
      console.log('Processing item for view:', {
        before: item,
        after: parsedItem
      });
      return parsedItem;
    });
    setEditedItems(itemsWithParsedQuantities);
    setIsEditing(false);
    setViewOrderDialogOpen(true);
  };

  const handleEditOrder = async () => {
    if (!selectedOrder || !editOrderName.trim()) {
      toast.error('Please enter a valid order name');
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        toast.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const updatedSavedOrders = (userData.savedOrders || []).map(order => 
        order.id === selectedOrder.id
          ? {
              ...order,
              name: editOrderName.trim(),
              items: editedItems.map(item => ({
                ...item,
                quantity: parseInt(item.quantity) || 1
              })),
              updatedAt: new Date().toISOString()
            }
          : order
      );

      await updateDoc(userDocRef, {
        savedOrders: updatedSavedOrders
      });

      toast.success('Order updated successfully');
      setIsEditing(false);
      setViewOrderDialogOpen(false);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    console.log('Quantity change - Before update:', {
      itemId,
      newQuantity,
      currentEditedItems: editedItems
    });

    setEditedItems(prevItems => {
      const updatedItems = prevItems.map(item => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            quantity: parseInt(newQuantity) || 0
          };
          console.log('Updating item:', {
            before: item,
            after: updatedItem
          });
          return updatedItem;
        }
        return item;
      });

      console.log('Quantity change - After update:', {
        updatedItems,
        changedItem: updatedItems.find(item => item.id === itemId)
      });

      return updatedItems;
    });
  };

  const handleViewPreviousOrder = (order) => {
    setSelectedOrder(order);
    setEditOrderName(order.id); // Using order ID as name since previous orders don't have names
    setEditedItems(order.items.map(item => ({
      ...item,
      quantity: parseInt(item.quantity) || 1
    })));
    setIsEditing(false);
    setViewOrderDialogOpen(true);
  };

  const handleDeleteItem = (itemId) => {
    setEditedItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const handleSavePreviousOrder = async (order) => {
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        toast.error('User document not found');
        return;
      }

      const userData = userDoc.data();
      const newSavedOrder = {
        ...order,
        name: `Saved from ${order.invoiceReference || order.id}`,
        savedAt: new Date().toISOString()
      };

      // Add to savedOrders array in user document
      const updatedSavedOrders = [...(userData.savedOrders || []), newSavedOrder];

      await updateDoc(userDocRef, {
        savedOrders: updatedSavedOrders
      });

      toast.success('Order saved successfully');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Orders</h1>
        {cartItems.length > 0 && (
          <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Save className="mr-2 h-4 w-4" />
                Save Current Cart
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Current Cart</DialogTitle>
                <DialogDescription>
                  Give your saved cart a name for future reference.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label htmlFor="orderName">Order Name</Label>
                <Input
                  id="orderName"
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  placeholder="Enter order name"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveOrder}>
                  Save Order
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Saved Orders Section */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Saved Orders</CardTitle>
          <CardDescription>
            Your saved carts that you can restore at any time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {savedOrders.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No saved orders yet. Save your current cart to see it here.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60%]">Name</TableHead>
                  <TableHead className="w-[10%]">Items</TableHead>
                  <TableHead className="w-[10%]">Date</TableHead>
                  <TableHead className="w-[10%] text-right">Total</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {savedOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.name}</TableCell>
                    <TableCell>{order.items.length} items</TableCell>
                    <TableCell>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">{calculateOrderTotal(order.items).toFixed(2)} NOK</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleViewOrder(order);
                          }}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleRestoreOrder(order.items);
                          }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Restore
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                onSelect={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Saved Order</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this saved order? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteOrder(order.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Previous Orders Section */}
      <Card>
        <CardHeader>
          <CardTitle>Previous Orders</CardTitle>
          <CardDescription>
            Your order history
          </CardDescription>
        </CardHeader>
        <CardContent>
          {previousOrders.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No previous orders found.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60%]">Invoice Reference</TableHead>
                  <TableHead className="w-[10%]">Items</TableHead>
                  <TableHead className="w-[10%]">Date</TableHead>
                  <TableHead className="w-[10%] text-right">Total</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previousOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.invoiceReference || order.id}</TableCell>
                    <TableCell>{order.items.length} items</TableCell>
                    <TableCell>
                      {new Date(order.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">{calculateOrderTotal(order.items).toFixed(2)} NOK</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleViewPreviousOrder(order);
                          }}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleRestoreOrder(order.items);
                          }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Restore
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.preventDefault();
                            handleSavePreviousOrder(order);
                          }}>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View/Edit Order Dialog */}
      <Dialog open={viewOrderDialogOpen} onOpenChange={setViewOrderDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedOrder?.name ? (isEditing ? 'Edit Order' : 'View Order') : 'View Previous Order'}
            </DialogTitle>
            <DialogDescription>
              {selectedOrder?.name 
                ? (isEditing ? 'Edit the order details below' : 'View and manage your saved order')
                : 'View your previous order details'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {selectedOrder.name && (
                <div className="space-y-2">
                  <Label htmlFor="editOrderName">Order Name</Label>
                  <div className="flex gap-2">
                    <Input
                      id="editOrderName"
                      value={editOrderName}
                      onChange={(e) => setEditOrderName(e.target.value)}
                      disabled={!isEditing || !selectedOrder.name}
                    />
                    {selectedOrder.name && !isEditing && (
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                    {selectedOrder.name && isEditing && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setEditOrderName(selectedOrder.name);
                          setEditedItems([...selectedOrder.items]);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium">Order Items</h3>
                  <span className="text-sm text-muted-foreground">
                    {editedItems.length} items
                  </span>
                </div>
                <ScrollArea className="h-[300px] rounded-md border p-4">
                  <div className="space-y-4">
                    {editedItems.map((item) => (
                      <div key={item.id} className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          {item.itemRemark && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.itemRemark}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4 min-w-[200px] justify-end">
                          <p className="text-sm text-muted-foreground w-[80px] text-right">
                            {item.price} NOK
                          </p>
                          {isEditing && selectedOrder.name ? (
                            <>
                              <div className="w-[100px]">
                                <input
                                  type="number"
                                  min="0"
                                  value={item.quantity}
                                  onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                  className="w-full h-7 px-1 text-center border rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.id)}
                                className="h-7 w-7 p-0 hover:bg-destructive/10 hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <p className="font-medium w-[100px] text-right">{item.quantity}x</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <Separator />

              <div className="flex justify-between items-center py-2">
                <span className="font-medium">Order Total</span>
                <span className="font-semibold text-lg">
                  {calculateOrderTotal(editedItems).toFixed(2)} NOK
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            {isEditing && selectedOrder.name ? (
              <Button onClick={handleEditOrder}>
                Save Changes
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    handleRestoreOrder(editedItems);
                    setViewOrderDialogOpen(false);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Restore to Cart
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewOrderDialogOpen(false)}
                >
                  Close
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage; 